import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, History, Loader2, Download } from "lucide-react";
import Layout from "@/components/Layout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const statusBadge = (status) => {
  const map = {
    pending: { className: "bg-amber-100 text-amber-800", label: "Pendente" },
    approved: { className: "bg-emerald-100 text-emerald-800", label: "Aprovado" },
    rejected: { className: "bg-red-100 text-red-800", label: "Rejeitado" },
  };
  const s = map[status] || map.pending;
  return <Badge className={s.className}>{s.label}</Badge>;
};

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyRange, setHistoryRange] = useState("7d");
  const [analysesLoading, setAnalysesLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/users/${id}/approve`); toast.success("Usuário aprovado"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const reject = async (id) => {
    try { await api.post(`/admin/users/${id}/reject`); toast.success("Usuário rejeitado"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/users/${confirmDelete.id}`);
      toast.success("Usuário excluído");
      setConfirmDelete(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openHistory = async (u) => {
    setHistoryUser(u);
    setHistoryRange("7d");
  };

  const downloadAdminCsv = async () => {
    if (!historyUser) return;
    try {
      const params = new URLSearchParams({ range: historyRange, kind: "all" });
      const token = localStorage.getItem("sensorize_token");
      const res = await fetch(`${API_BASE}/history/user/${historyUser.id}/export.csv?${params.toString()}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Falha ao exportar CSV");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `historico_${(historyUser.email || "usuario").split("@")[0]}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      toast.success("CSV baixado");
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    if (!historyUser) return;
    let cancelled = false;
    setAnalysesLoading(true);
    api.get(`/history/user/${historyUser.id}`, { params: { range: historyRange } })
      .then(({ data }) => { if (!cancelled) setHistoryData(data); })
      .catch((err) => { if (!cancelled) toast.error(formatApiError(err)); })
      .finally(() => { if (!cancelled) setAnalysesLoading(false); });
    return () => { cancelled = true; };
  }, [historyUser, historyRange]);

  return (
    <Layout>
      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Painel Administrativo</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 mt-1">
          Usuários e histórico
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Aprove novos cadastros, remova acessos e visualize o histórico de análises por usuário.
        </p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Papel</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-semibold">{u.name}</TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <Badge className={u.role === "admin" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}>
                        {u.role === "admin" ? "Admin" : "Usuário"}
                      </Badge>
                    </TableCell>
                    <TableCell>{statusBadge(u.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-2 flex-wrap justify-end">
                        {u.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => approve(u.id)} data-testid={`approve-${u.email}`}
                              className="bg-[#7CB342] hover:bg-[#558B2F] text-white">
                              <Check className="w-4 h-4 mr-1" /> Aprovar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => reject(u.id)} data-testid={`reject-${u.email}`}
                              className="border-red-200 text-red-700 hover:bg-red-50">
                              <X className="w-4 h-4 mr-1" /> Rejeitar
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openHistory(u)} data-testid={`history-${u.email}`}>
                          <History className="w-4 h-4 mr-1" /> Histórico
                        </Button>
                        {u.id !== user.id && (
                          <Button size="sm" variant="outline" onClick={() => setConfirmDelete(u)} data-testid={`delete-${u.email}`}
                            className="border-red-200 text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
            <AlertDialogDescription>
              Excluir <strong>{confirmDelete?.name}</strong> ({confirmDelete?.email})? Todas as análises deste
              usuário também serão removidas. Esta ação é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-button" className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History dialog */}
      <Dialog open={!!historyUser} onOpenChange={(o) => { if (!o) { setHistoryUser(null); setHistoryData(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Histórico de análises — {historyUser?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="admin-history-range-filter">
            {[
              { id: "24h", label: "24 horas" },
              { id: "7d", label: "7 dias" },
              { id: "30d", label: "30 dias" },
              { id: "all", label: "Tudo" },
            ].map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setHistoryRange(r.id)}
                data-testid={`admin-range-${r.id}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  historyRange === r.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {r.label}
              </button>
            ))}
            <Button
              size="sm"
              onClick={() => downloadAdminCsv()}
              disabled={!historyUser}
              data-testid="admin-export-csv"
              className="ml-auto bg-[#7CB342] hover:bg-[#558B2F] text-white h-7 text-xs"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Exportar CSV
            </Button>
          </div>

          {historyData?.counts && (
            <div className="flex gap-3 text-xs text-slate-500 mb-2">
              <span>Total: <strong className="text-slate-900">{historyData.counts.total}</strong></span>
              <span>ANOVA: <strong className="text-[#E65C00]">{historyData.counts.anova}</strong></span>
              <span>Escolha Forçada: <strong className="text-[#558B2F]">{historyData.counts.sensory}</strong></span>
            </div>
          )}

          {analysesLoading ? (
            <div className="py-8 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
            </div>
          ) : !historyData?.items || historyData.items.length === 0 ? (
            <p className="text-slate-500 py-8 text-center">Nenhuma análise encontrada no período selecionado.</p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Módulo</TableHead>
                  <TableHead>Detalhe</TableHead>
                  <TableHead>Resultado</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {historyData.items.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                        {new Date(a.created_at).toLocaleString("pt-BR")}
                      </TableCell>
                      <TableCell>
                        <Badge className={a.type === "anova" ? "bg-[#FFF1E6] text-[#E65C00]" : "bg-[#EEF7E4] text-[#558B2F]"}>
                          {a.type === "anova" ? "ANOVA" : "Escolha Forçada"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {a.type === "anova" ? (
                          <span>
                            {a.title ? <strong>{a.title}</strong> : null}
                            {a.title ? " · " : ""}{a.test_name} · {a.post_hoc}
                          </span>
                        ) : (
                          <span>{a.test_label} · {a.mode === "difference" ? "Diferença" : "Similaridade"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <Badge className={a.summary?.significant ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                          {a.type === "anova"
                            ? (a.summary?.significant ? "Significativo" : "Não significativo")
                            : (a.summary?.decision || "—")}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
